import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { endpoint } from './endpoint';
import { getToken } from '../utils/localStorage';
export interface Category {
  id: number;
  level: number;
  name: string;
  parent_category: number | null;
}

/** A category as the navigator returns it, with its children already nested. */
export interface CategoryNode {
  id: number;
  name: string;
  parent_id: number | null;
  depth: number;
  /** Scripts filed on this category itself. */
  script_count: number;
  /** Scripts filed on this category or anywhere beneath it. */
  total_script_count: number;
  children: CategoryNode[];
}

export interface CategoryNavigator {
  tree: CategoryNode[];
  /** Total categories in the table, which may exceed the number returned. */
  category_count: number;
  /** How many nodes the response actually carries. */
  returned_category_count: number;
  /** True when the tree was capped, so the counts describe more than is shown. */
  truncated: boolean;
  uncategorised_script_count: number;
}

const api = createApi({
  reducerPath: 'Categoryapi',
  baseQuery: async (args, api, extraOptions) => {
    const token = getToken();
    const headers = {
      ...args.headers,
      Authorization: `Bearer ${token}`,
    };
    return fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL })(
      { ...args, headers },
      api,
      extraOptions,
    );
  },
  tagTypes: ['GET', 'Category', 'Script'],
  endpoints: (builder) => ({
    create: builder.mutation({
      query: ({ token, data }) => ({
        url: `${endpoint.category}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Category'],
    }),

    update: builder.mutation({
      query: ({ token, id, data }) => ({
        url: `${endpoint.category}/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Category'],
    }),
    remove: builder.mutation({
      query: ({ token, id }) => ({
        url: `${endpoint.category}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Category'],
    }),
    getAllCategory: builder.query({
      query: () => ({
        url: endpoint.category,
        method: 'GET',
      }),
      providesTags: ['Category'],
    }),
    // The whole tree in one response, to whatever depth it has, with the number
    // of scripts on each category and the number anywhere beneath it. Replaces
    // fetching a flat list and rebuilding the hierarchy in the browser.
    getCategoryNavigator: builder.query<CategoryNavigator, void>({
      query: () => ({
        url: `${endpoint.category}/navigator`,
        method: 'GET',
      }),
      providesTags: ['Category'],
    }),
  }),
});

export const {
  useCreateMutation,
  useUpdateMutation,
  useRemoveMutation,
  useGetAllCategoryQuery,
  useGetCategoryNavigatorQuery,
} = api;
export default api;
